import React from 'react';
import getConfig from 'next/config';
import { GetServerSideProps } from 'next';
import BasicLayout from 'layout/Basic';
import ArticleDetails, { Props as ArticleDetailsProps } from 'containers/Article/Details';
import { convertProps } from 'lib/parseAPIResponse';
import { NextSeo, ArticleJsonLd } from 'next-seo';
import ForbiddenPage from 'pages/403';

interface Props extends ArticleDetailsProps {
    error?: {
        code: number;
        message: string;
    };
}

export const ArticleDetailsPage = (props: Props): React.JSX.Element => {
    if (props?.error?.code === 403) {
        return <ForbiddenPage message="Przepraszamy, ale nie masz dostępu do artykułu." />;
    }
    const {
        article: {
            openingImage,
            openingImageInfo,
            seoSlug,
            seoTitle,
            seoDescription,
            title,
            teaser,
            category,
            dateDisplayed,
            headline,
            tags = [],
            authors = [],
            noIndex
        },
        ads
    } = props;

    const description = seoDescription || teaser?.replace(/<[^>]*>?/gm, '');
    const { url, name } = getConfig().publicRuntimeConfig;
    return (
        <BasicLayout ads={ads}>
            <NextSeo
                noindex={noIndex}
                title={seoTitle || title}
                canonical={`${url}/artykuly/${seoSlug}`}
                description={description}
                openGraph={{
                    title: title,
                    description: description,
                    url: `${url}/artykuly/${seoSlug}`,
                    type: 'article',
                    article: {
                        publishedTime: dateDisplayed,
                        modifiedTime: dateDisplayed,
                        section: category.title,
                        authors: authors.map((author) => `${url}/autorzy/${author.seoSlug}`),
                        tags: tags.map((tag) => tag.name)
                    },
                    images: [
                        {
                            url: `${url}/${openingImage}`,
                            width: openingImageInfo?.width || 567,
                            height: openingImageInfo?.height || 359,
                            alt: openingImageInfo?.alt || title
                        }
                    ]
                }}
            />
            <ArticleJsonLd
                url={`${url}/artykuly/${seoSlug}`}
                title={title}
                images={[`${url}/${openingImage}`]}
                datePublished={dateDisplayed}
                dateModified={dateDisplayed}
                authorName={authors.map((author) => ({
                    name: author.name,
                    url: `${url}/autorzy/${author.seoSlug}`
                }))}
                publisherName={name}
                publisherLogo={`${url}/static/images/logo.svg`}
                headline={headline}
                description={description}
            />
            <ArticleDetails {...props} />
        </BasicLayout>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ params, query }) => {
    let slug = params?.slug || '';
    if (Array.isArray(slug)) {
        slug = slug[0];
    }
    if (slug.includes(',')) {
        slug = slug.substr(0, params.slug.indexOf(','));
    }
    const queryParams = query._previewKey ? `?_previewKey=${query._previewKey}` : '';
    const { apiUrl } = getConfig().publicRuntimeConfig;

    const path = `/public/api/articles/${slug}`;
    const url = apiUrl + path;
    return Promise.all([
        fetch(url + queryParams),
        fetch(`${apiUrl}/public/api/naszadsik?path=articles/${slug}`)
    ]).then(async ([res1, res2]) => {
        const data = await res1.json();
        if (data.code === 404) {
            return {
                notFound: true
            };
        }
        if (data.code === 403) {
            return {
                props: {
                    error: { ...convertProps(data) }
                }
            };
        }
        const ads = await res2.json();

        return { props: { ...convertProps(ads), ...convertProps(data) } };
    });
};

export default ArticleDetailsPage;
